import { useEffect, useState } from "react";
import { DollarSign } from 'lucide-react';
import { db, auth } from '../../Config/Config';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';

const InvestmentPlans = () => {
  const [amount, setAmount] = useState({
    beginners: 0,
    advanced: 0,
    premium: 0,
  });

  const [totalAmount, setTotalAmount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [AccountBalance, setAccountBalance] = useState(0);

  useEffect(() => {
    const fetchTotalAmount = async () => {
      const user = auth.currentUser;
      if (!user) return; // Silently return if not logged in

      const depositsQuery = query(
        collection(db, 'deposits'),
        where('uid', '==', user.uid)
      );

      const depositsSnapshot = await getDocs(depositsQuery);
      depositsSnapshot.forEach((docSnapshot) => {
        const depositData = docSnapshot.data();
        setTotalAmount(depositData.TotalAmount);
      });
    };

    fetchTotalAmount();
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const accountBalanceQuery = query(
        collection(db, "AccountBalance"),
        where("uid", "==", user.uid)
      );

      const unsubscribe = onSnapshot(accountBalanceQuery, (accountBalanceSnapshot) => {
        if (!accountBalanceSnapshot.empty) {
          setAccountBalance(accountBalanceSnapshot.docs[0].data().AccountBalance);
        } else {
          setAccountBalance(0);
        }
      });

      return () => unsubscribe();
    }
  }, []);

  const handleInputChange = (e, plan) => {
    setAmount({ ...amount, [plan]: e.target.value });
  };

  const handleInvest = async (plan, roi, titleplan, min, max) => {
    const user = auth.currentUser;
    if (!user) {
      setModalMessage('Please log in to invest.');
      setIsModalOpen(true);
      return;
    }

    if (AccountBalance !== 0) {
      setModalMessage("You have an ongoing Plan");
      setIsModalOpen(true);
      return;
    }

    const investmentAmount = parseFloat(amount[plan]);
    if (isNaN(investmentAmount) || investmentAmount <= 0) {
      setModalMessage('Please enter a valid investment amount.');
      setIsModalOpen(true);
      return;
    }
  
    if (investmentAmount < min) {
      setModalMessage(`The amount you entered is below the minimum allowed for the ${plan.toUpperCase()} plan. Please invest at least $${min}.`);
      setIsModalOpen(true);
      return;
    }
    
    if (investmentAmount > max) {
      setModalMessage(`The amount you entered exceeds the maximum allowed for the ${plan.toUpperCase()} plan. Please invest no more than $${max}.`);
      setIsModalOpen(true);
      return;
    }

    try {
      const depositsQuery = query(
        collection(db, 'deposits'),
        where('uid', '==', user.uid)
      );
      const depositsSnapshot = await getDocs(depositsQuery);
      let totalAmountInDb = null;

      depositsSnapshot.forEach((docSnapshot) => {
        const depositData = docSnapshot.data();
        if (depositData && depositData.TotalAmount !== undefined) {
          totalAmountInDb = depositData.TotalAmount;
        }
      });
      
      if (totalAmountInDb === null) {
        alert('TotalAmount field does not exist in your deposits. Please check your account.');
        return;
      }
  
      if (totalAmountInDb < investmentAmount) {
        setModalMessage('Insufficient funds. Your total deposits are less than the amount you wish to invest.');
        setIsModalOpen(true);
        return;
      }
  
      const TotalEarnings = investmentAmount * roi;
      const AccountBalance = investmentAmount + TotalEarnings;
  
      const newTotalAmount = totalAmountInDb - investmentAmount;
      await updateDoc(doc(db, 'deposits', depositsSnapshot.docs[0].id), {
        TotalAmount: newTotalAmount,
      });
      setTotalAmount(newTotalAmount);
  
      const accountBalanceDoc = doc(db, 'AccountBalance', user.uid);
      const accountBalanceSnapshot = await getDoc(accountBalanceDoc);
  
      if (accountBalanceSnapshot.exists()) {
        await updateDoc(accountBalanceDoc, {
          AccountBalance: accountBalanceSnapshot.data().AccountBalance + AccountBalance,
          TotalEarnings: accountBalanceSnapshot.data().TotalEarnings + TotalEarnings,
          investmentAmount: accountBalanceSnapshot.data().investmentAmount + investmentAmount,
          titleplan,  
        });
      } else {
        await setDoc(accountBalanceDoc, {
          uid: user.uid,
          AccountBalance,
          TotalEarnings,
          investmentAmount,
          titleplan,  
        });
      }
  
      const usersQuery = query(
        collection(db, 'users'),
        where('uid', '==', user.uid)
      );
      const usersSnapshot = await getDocs(usersQuery);
  
      if (!usersSnapshot.empty) {
        const userData = usersSnapshot.docs[0].data();
        const { firstName, lastName } = userData;
  
        const presentDate = new Date();
        const durationDays = parseInt(plan === 'beginners' ? 10 : plan === 'advanced' ? 15 : 20);
        const futureDate = new Date(presentDate);
        futureDate.setDate(futureDate.getDate() + durationDays);
  
        const historyDoc = doc(db, 'history', user.uid);
        await setDoc(historyDoc, {
          futureDate: futureDate.toISOString(),
          status: false,
          firstName,
          lastName,
          uid: user.uid,
        }, { merge: true });
  
        setModalMessage(`Successfully invested $${investmentAmount} in the ${plan.toUpperCase()} plan!`);
        setIsModalOpen(true);
      } else {
        setModalMessage('User data not found in the database.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Error updating account balance: ', error);
      setModalMessage('Failed to invest. Please try again later.');
      setIsModalOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#000300] pb-20 pt-12">
      {/* Premium Header */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-lg font-light text-white/70 mb-2">WEALTH GROWTH SOLUTIONS</p>
          <h1 className="text-5xl font-medium text-white tracking-wide mb-4">
            <span className="bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">Investment</span> Plans
          </h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-6"></div>
          <p className="text-white/60 font-light max-w-2xl mx-auto text-xl">
            Strategically designed portfolios for discerning investors
          </p>
        </div>

        {/* Total Deposits - Luxury Card */}
        <div className="flex justify-center mb-20">
          <div className="bg-[#000300] p-8 rounded-xl border border-white/90 shadow-2xl w-full max-w-md relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:3px_3px] opacity-20"></div>
            <div className="relative flex items-center justify-between">
                <div>
                  <p className="text-white/70 font-medium">Total Portfolio Value</p>
                  <p className="text-4xl font-light text-white mt-2">${totalAmount}</p>
                </div>
                <div className="bg-white p-4 rounded-full border border-white/10">
                  <DollarSign className="h-8 w-8 text-[#00C853]" />
                </div>
            </div>
            <div className="relative mt-6 pt-6 border-t border-white/10">
              <p className="text-sm text-white/60">Your assets are growing at <span className="text-white">5.8%</span> annually</p>
            </div>
          </div>
        </div>

        {/* Luxury Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Basic Plan */}
          <div className="bg-[#000300] rounded-xl border border-white/10 overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-300">
            <div className="h-[2px] bg-gradient-to-r from-transparent via-white to-transparent"></div>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-medium text-white">BEGINNERS</h3>
                <span className="bg-white/5 text-white/90 px-3 py-1 rounded-full text-sm border border-white/10">ENTRY</span>
              </div>
              
              <div className="mb-8">
                <p className="text-white/60 mb-1">Investment Range</p>
                <p className="text-white text-xl">$100 - $999</p>
              </div>
              
              <div className="flex justify-between items-center mb-8">
                <div>
                  <p className="text-white/60"> ROI</p>
                  <p className="text-white text-2xl">100%</p>
                </div>
                <div>
                  <p className="text-white/60">Duration</p>
                  <p className="text-white">10 Days</p>
                </div>
              </div>
              
              <div className="mb-8">
                <label className="block text-white/60 mb-2">Amount ($)</label>
                <input
                  type="number"
                  value={amount.beginners}
                  onChange={(e) => handleInputChange(e, 'beginners')}
                  className="w-full bg-[#000300] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                  placeholder="Enter amount"
                />
              </div>
              
              <button 
                onClick={() => handleInvest('beginners', 1, 'BEGINNERS PLAN', 100, 999)}
                className="w-full bg-white/5 hover:bg-white/10 text-white py-3 px-6 rounded-lg border border-white/10 hover:border-white/20 transition-all duration-300 shadow-lg font-medium relative overflow-hidden"
              >
                <span className="relative z-10">Invest Now</span>
                <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>

          {/* Advanced Plan - Featured */}
          <div className="relative bg-[#000300] rounded-xl border border-white/20 overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-300">
            <div className="h-[2px] bg-white"></div>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-medium text-white pr-4">ADVANCED</h3>
                <span className="bg-white text-[#000300] px-3 py-1 rounded-full text-sm font-medium">Popular</span>
              </div>
              
              <div className="mb-8">
                <p className="text-white/60 mb-1">Investment Range</p>
                <p className="text-white text-xl">$1,000 - $9,999</p>
              </div>
              
              <div className="flex justify-between items-center mb-8">
                <div>
                  <p className="text-white/60"> ROI</p>
                  <p className="text-white text-2xl">300%</p>
                </div>
                <div>
                  <p className="text-white/60">Duration</p>
                  <p className="text-white">15 Days</p>
                </div>
              </div>
              
              <div className="mb-8">
                <label className="block text-white/60 mb-2">Amount ($)</label>
                <input
                  type="number"
                  value={amount.advanced}
                  onChange={(e) => handleInputChange(e, 'advanced')}
                  className="w-full bg-[#000300] border border-white/20 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                  placeholder="Enter amount"
                />
              </div>
              
              <button 
                onClick={() => handleInvest('advanced', 3, 'ADVANCED PLAN', 1000, 9999)}
                className="w-full bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 shadow-lg font-medium relative overflow-hidden"
              >
                <span className="relative z-10">Invest Now</span>
                <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(0,3,0,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>

          {/* Elite Plan */}
          <div className="bg-[#000300] rounded-xl border border-white/10 overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-300">
            <div className="h-[2px] bg-gradient-to-r from-transparent via-white to-transparent"></div>
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-medium text-white">PREMIUM</h3>
                <span className="bg-white/5 text-white/90 px-3 py-1 rounded-full text-sm border border-white/10">VIP</span>
              </div>
              
              <div className="mb-8">
                <p className="text-white/60 mb-1">Investment Range</p>
                <p className="text-white text-xl">$10,000 - $100,000</p>
              </div>
              
              <div className="flex justify-between items-center mb-8">
                <div>
                  <p className="text-white/60"> ROI</p>
                  <p className="text-white text-2xl">500%</p>
                </div>
                <div>
                  <p className="text-white/60">Duration</p>
                  <p className="text-white">20 Days</p>
                </div>
              </div>
              
              <div className="mb-8">
                <label className="block text-white/60 mb-2">Amount ($)</label>
                <input
                  type="number"
                  value={amount.premium}
                  onChange={(e) => handleInputChange(e, 'premium')}
                  className="w-full bg-[#000300] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                  placeholder="Enter amount"
                />
              </div>
              
              <button 
                onClick={() => handleInvest('premium', 5, 'PREMIUM PLAN', 10000, 100000)}
                className="w-full bg-white/5 hover:bg-white/10 text-white py-3 px-6 rounded-lg border border-white/10 hover:border-white/20 transition-all duration-300 shadow-lg font-medium relative overflow-hidden"
              >
                <span className="relative z-10">Invest Now</span>
                <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Luxury Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-[#000300] rounded-xl max-w-md w-full border border-white/10 overflow-hidden shadow-2xl">
            <div className="h-[2px] bg-white"></div>
            <div className="p-8">
              <h3 className="text-2xl font-medium text-white mb-2">Investment Confirmation</h3>
              <p className="text-white/70 mb-6">{modalMessage}</p>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="w-full bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentPlans;